import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Turnstile } from '@marsidev/react-turnstile';
import { Button, Form, Input } from 'antd';
import { ToastContainer, toast } from "react-toastify";

import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { FaLock, FaUser } from "react-icons/fa";

import 'react-toastify/dist/ReactToastify.css';
import '../../../css/UserAdmin/AdminLogin.css';

import SJDEFILogo from '../../../images/SJDEFILogo.png';
import Footer from '../Footer';

const LOCKOUT_STORAGE_KEY = 'adminLoginLockout';

const AdminLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [captchaToken, setCaptchaToken] = useState(null);
  const [isTurnstileReady, setIsTurnstileReady] = useState(true);
  const [captchaWidgetKey, setCaptchaWidgetKey] = useState(Date.now());

  const [isLockedOut, setIsLockedOut] = useState(false);
  const [lockoutMessage, setLockoutMessage] = useState('');
  const [remainingLockoutTime, setRemainingLockoutTime] = useState(0);
  const lockoutTimerRef = useRef(null);

  const clearLockoutState = (showToast = false) => {
    setIsLockedOut(false);
    setLockoutMessage('');
    setRemainingLockoutTime(0);
    localStorage.removeItem(LOCKOUT_STORAGE_KEY);
    if (lockoutTimerRef.current) {
      clearInterval(lockoutTimerRef.current);
    }
    if (showToast) {
      toast.info("Access lockout has expired. You can try logging in again.");
    }
    setCaptchaToken(null);
    setCaptchaWidgetKey(Date.now());
  };

  useEffect(() => {
    const storedLockout = localStorage.getItem(LOCKOUT_STORAGE_KEY);
    if (storedLockout) {
      try {
        const { lockedUntilTimestamp, message } = JSON.parse(storedLockout);
        const now = Date.now();
        if (lockedUntilTimestamp > now) {
          const remainingMs = lockedUntilTimestamp - now;
          setLockoutMessage(message || "Your account is temporarily locked.");
          startLockoutTimer(remainingMs, message, false);
        } else {
          clearLockoutState();
        }
      } catch (e) {
        clearLockoutState();
      }
    }
  }, []);

  useEffect(() => {
    return () => {
      if (lockoutTimerRef.current) {
        clearInterval(lockoutTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (location.state?.fromPasswordReset) {
      toast.success('Password reset successful. Please check your email for the new password.');
    }
    if (location.state?.fromVerification) {
      toast.success('Email verification successful! Please login.');
    }
    if (location.state?.accountInactive) {
      toast.error('Your session was invalidated. Please login again.');
    }
    if (location.state && Object.keys(location.state).length > 0) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  const startLockoutTimer = (ms, messageFromServer = "", saveToLocalStorage = true) => {
    if (ms <= 0) {
      clearLockoutState(true);
      return;
    }
    const currentMessage = messageFromServer || "This account is temporarily locked due to too many failed attempts.";
    setIsLockedOut(true);
    setLockoutMessage(currentMessage);
    setRemainingLockoutTime(ms);

    if (saveToLocalStorage) {
      const lockedUntilTimestamp = Date.now() + ms;
      localStorage.setItem(LOCKOUT_STORAGE_KEY, JSON.stringify({
        lockedUntilTimestamp,
        message: currentMessage
      }));
    }
    if (lockoutTimerRef.current) clearInterval(lockoutTimerRef.current);
    lockoutTimerRef.current = setInterval(() => {
      setRemainingLockoutTime(prevTime => {
        const newTime = prevTime - 1000;
        if (newTime <= 0) {
          clearLockoutState(true);
          return 0;
        }
        return newTime;
      });
    }, 1000);
  };

  const formatTime = (ms) => {
    if (ms <= 0) return "0s";
    const totalSeconds = Math.ceil(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes > 0 ? `${minutes}m ` : ''}${seconds}s`;
  };

  const handleTurnstileError = () => {
    setFieldErrors(prev => ({ ...prev, captcha: 'CAPTCHA widget error. Please try reloading.' }));
    toast.error("An error occurred with the CAPTCHA widget. Please try reloading the page.");
    setIsTurnstileReady(false);
    setCaptchaToken(null);
  };

  const handleTurnstileExpire = () => {
    setCaptchaToken(null);
    setFieldErrors(prev => ({ ...prev, captcha: 'CAPTCHA expired. Please verify again.' }));
    toast.warn("CAPTCHA expired. Please verify again.");
    setCaptchaWidgetKey(Date.now());
  };

  const sendSystemLog = async (payload) => {
    // Only send log if action is set
    if (!payload.action) return;
    try {
      const logResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/system-logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (logResponse.ok) {
        const logResData = await logResponse.json(); // Optional
        console.log('System log recorded:', logResData.message || logResData); // Optional
      } else {
        const errData = await logResponse.json().catch(() => ({ message: 'Failed to parse log error response' })); // Optional
        console.error('Failed to record system log - Server:', logResponse.status, errData.message || errData); // Optional
      }
    } catch (logErr) {
      console.error('Failed to record system log - Network/Fetch:', logErr); // Optional
    }
  };

  const handleSubmit = async (formValues) => {
    setFieldErrors({});
    let logPayload = {
      userID: 'N/A',
      accountName: formValues.email.trim(),
      role: 'N/A',
      action: '', // Intentionally blank, set only on definitive outcomes
      detail: ''
    };

    if (isLockedOut) {
      toast.error(lockoutMessage || `Account locked. Please wait ${formatTime(remainingLockoutTime)}.`);
      // No log from client if UI is locked; server handles attempts against locked accounts.
      return;
    }

    if (!captchaToken) {
      setFieldErrors(prev => ({ ...prev, captcha: 'Please complete the CAPTCHA verification.' }));
      if (!toast.isActive('captchaError')) {
        toast.error('Please complete the CAPTCHA verification.', { toastId: 'captchaError' });
      }
      logPayload.action = 'Login Failed';
      logPayload.detail = `Login attempt for ${formValues.email.trim()}: CAPTCHA not completed.`;
      await sendSystemLog(logPayload);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formValues.email.trim(),
          password: formValues.password,
          captchaToken,
        }),
      });
      const data = await response.json();

      const serverUserID = data.userID || 'N/A';
      const serverFirstName = data.firstName || '';
      const serverLastName = data.lastName || '';
      const serverRole = data.role || 'N/A';
      const serverAccountName = serverFirstName ? `${serverFirstName} ${serverLastName}`.trim() : formValues.email.trim();

      // Update payload with identified user info, if any
      logPayload.userID = serverUserID;
      logPayload.accountName = serverAccountName;
      logPayload.role = serverRole;

      // Reset CAPTCHA if response is not OK, OR if it's OK but requires another OTP step
      if (!response.ok || (response.ok && data.errorType === 'login_otp_required')) {
        setCaptchaToken(null);
        setCaptchaWidgetKey(Date.now());
      }

      if (!response.ok) {
        logPayload.action = 'Login Failed'; // Set action for all non-OK responses

        if (data.errorType === 'account_locked') {
          startLockoutTimer(data.remainingTime, data.message);
          logPayload.detail = `Login attempt for ${logPayload.accountName} (${logPayload.userID}): Account Locked by server. Message: ${data.message}`;
        } else if (data.errorType === 'pending_verification') {
          toast.info(data.message || 'Account requires verification.');
          // NO LOG FROM HERE for pending_verification. VerifyEmail.js will handle its own logs.
          logPayload.action = ''; // Clear action so no log is sent from this branch
          navigate('/admin/verify-email', {
            state: { email: data.email, firstName: data.firstName, fromLogin: true, isPasswordReset: false },
          });
        } else if (data.errorType === 'authentication') {
          logPayload.detail = `Login attempt for ${logPayload.accountName} (${logPayload.userID}): Invalid Credentials. Server: ${data.message}`;
          toast.error(data.message || 'Invalid credentials.');
        } else {
          toast.error(data.message || 'Login failed. Please try again.');
          logPayload.detail = `Login attempt for ${logPayload.accountName} (${logPayload.userID}): Server Error (Status ${response.status}). Message: ${data.message || 'Unknown error.'}`;
          if (data.message && data.message.toLowerCase().includes('captcha')) {
            setFieldErrors(prev => ({ ...prev, captcha: data.message || 'CAPTCHA verification failed. Please retry.' }));
            logPayload.detail = `Login attempt for ${formValues.email.trim()}: Server CAPTCHA Error. Message: ${data.message}`;
          }
        }
        await sendSystemLog(logPayload); // Will only send if logPayload.action is set
        return;
      }

      // Response is OK
      if (data.errorType === 'login_otp_required') {
        toast.info(data.message || 'Additional verification required.');
        // NO LOG FROM HERE for login_otp_required. VerifyLoginOtp.js will handle its own logs.
        navigate('/admin/verify-login-otp', {
          state: {
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
            userID: data.userID,
            role: data.role
          },
        });
        return;
      }

      // Login is fully successful (Direct, no OTP step FROM THIS COMPONENT)
      clearLockoutState();
      localStorage.setItem('fullName', `${data.firstName} ${data.lastName || ''}`);
      localStorage.setItem('role', data.role || 'ROLE');
      localStorage.setItem('userID', data.userID);
      localStorage.setItem('userEmail', data.email);
      localStorage.setItem('id', data._id);
      localStorage.setItem('token', data.token);

      logPayload.action = 'Logged In';
      logPayload.detail = `User ${logPayload.accountName} (${logPayload.userID}) logged in successfully (Direct).`;
      await sendSystemLog(logPayload);

      toast.success('Login successful!');
      navigate('/admin/dashboard');

    } catch (err) {
      toast.error(err.message || 'Login failed. Check connection or try again.');
      logPayload.action = 'Login Failed';
      logPayload.detail = `Client-side error during login attempt for ${formValues.email.trim()}. Error: ${err.message}`;
      await sendSystemLog(logPayload);
      setCaptchaToken(null);
      setCaptchaWidgetKey(Date.now());
    }
  };

  const onFinishFailed = (errorInfo) => {
    if (!toast.isActive('formValidationError')) {
      toast.error('Please correct the errors highlighted in the form.', { toastId: 'formValidationError' });
    }
  };

  const handleGoToHome = () => navigate('/home');
  const handleForgotPassword = () => navigate('/admin/forgot-password');

  const lockoutMessageStyle = {
    color: 'red',
    backgroundColor: '#ffe6e6',
    padding: '10px 15px',
    borderRadius: '4px',
    marginBottom: '20px',
    textAlign: 'center',
    fontWeight: 'bold',
    border: '1px solid red'
  };

  return (
    <div className="admin-login-page-container">
      <ToastContainer position="top-right" autoClose={4000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
      <div className="admin-login-main-content-wrapper">
        <div className="admin-login-content-box" style={{ borderRadius: '15px' }}>
          <div className="admin-login-left-panel">
            <img src={SJDEFILogo} alt="SJDEFI Logo" className="admin-login-logo-img" />
            <div className="admin-login-header-text-group">
              <h1>SAN JUAN DE DIOS EDUCATIONAL FOUNDATION, INC.</h1>
              <p className="admin-login-motto-text">Where faith and reason are expressed in Charity.</p>
            </div>
            <h1 className='admin-login-portal-title'>Admin Online Portal</h1>
          </div>
          <div className="admin-login-right-panel">
            <h1 className='admin-login-form-title'>Login</h1>
            {isLockedOut && (
              <div style={lockoutMessageStyle}>
                {lockoutMessage}
                {remainingLockoutTime > 0 && (
                  <div style={{ fontSize: '0.9em', marginTop: '5px' }}>
                    (Please try again in {formatTime(remainingLockoutTime)})
                  </div>
                )}
              </div>
            )}
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              onFinishFailed={onFinishFailed}
              autoComplete="off"
            >
              <Form.Item
                label={<span className="admin-login-input-label">Email</span>}
                name="email"
                rules={[
                  { required: true, message: 'Email is required' },
                  { type: 'email', message: 'Invalid email format' }
                ]}
                initialValue={email}
              >
                <Input
                  id="admin-email"
                  className="admin-login-custom-input-field"
                  addonBefore={<FaUser />}
                  placeholder="Enter Email"
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLockedOut}
                />
              </Form.Item>
              <Form.Item
                label={<span className="admin-login-input-label">Password</span>}
                name="password"
                style={{ marginTop: '15px' }}
                rules={[{ required: true, message: 'Password is required' }]}
                initialValue={password}
              >
                <Input.Password
                  id="admin-password"
                  className="admin-login-custom-input-field"
                  addonBefore={<FaLock />}
                  placeholder="Enter Password"
                  onChange={(e) => setPassword(e.target.value)}
                  iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                  disabled={isLockedOut}
                />
              </Form.Item>
              <div className="admin-login-options-links">
                <Button type="link" onClick={handleGoToHome} style={{ padding: 0 }}>
                  Go Back to Home
                </Button>
                <Button type="link" onClick={handleForgotPassword} style={{ padding: 0 }} disabled={isLockedOut}>
                  Forgot Password?
                </Button>
              </div>
              <div className={`admin-login-captcha-wrapper ${fieldErrors.captcha ? 'captcha-error-highlight' : ''}`} style={{ marginTop: '15px' }}>
                {isTurnstileReady ? (
                  <Turnstile
                    key={captchaWidgetKey}
                    siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY || "1x00000000000000000000AA"}
                    onSuccess={(token) => {
                      setCaptchaToken(token);
                      if (fieldErrors.captcha) setFieldErrors(prev => ({ ...prev, captcha: null }));
                    }}
                    onError={handleTurnstileError}
                    onExpire={handleTurnstileExpire}
                    options={{ theme: 'light', size: 'normal', retry: 'auto', 'retry-interval': 3000 }}
                    scriptOptions={{ async: true, defer: true, appendTo: 'body' }}
                  />
                ) : (
                  <div className="admin-login-captcha-message-error">
                    CAPTCHA service is currently unavailable. Please try again later.
                  </div>
                )}
              </div>
              {fieldErrors.captcha && (
                <div className="admin-login-captcha-message-error" style={{ color: 'red', marginTop: '5px' }}>{fieldErrors.captcha}</div>
              )}
              <Form.Item style={{ marginTop: '20px' }}>
                <Button
                  type='ghost'
                  htmlType="submit"
                  className="admin-login-submit-button"
                  block
                  disabled={isLockedOut || !isTurnstileReady}
                >
                  {isLockedOut ? `LOCKED (${formatTime(remainingLockoutTime)})` : 'Login'}
                </Button>
              </Form.Item>
            </Form>
          </div>
        </div>
      </div>
      <Footer className="admin-footer" />
    </div>
  );
};

export default AdminLogin;